//@ts-check
import * as et from '../../application/EventTypes';
import { InstanceTreeStorage } from '../../wgs/scene/InstanceTreeStorage';
import { EventDispatcher } from "../../application/EventDispatcher";

/** @typedef {import('../../application/Model').Model} Model */
/** @typedef {import('./RcsPackage').RcsPackage} RcsPackage */
/** @typedef {import('../../wgs/scene/ObjectTree').ObjectTree} ObjectTree */

/**
 * Single property entry returned by the RCS property database.
 * @typedef {Object} RcsPropertyEntry
 * @property {string} displayName - UI label shown in the property panel
 * @property {string|number} displayValue - Value shown in the property panel
 * @property {string} displayCategory - Grouping category in the property panel
 * @property {string} attributeName - Internal attribute key used for search and filtering
 * @property {number} type - Autodesk property data type code
 * @property {string|null} units - Unit string, or null if dimensionless
 * @property {boolean} hidden - Whether the property is hidden from default display
 * @property {number} precision - Decimal precision for numeric display
 */

/**
 * Property payload for a single instance-tree node.
 * @typedef {Object} RcsPropertyData
 * @property {number} dbId - Property-database node ID
 * @property {string} name - Node display name
 * @property {string} externalId - Stable external identifier for the node
 * @property {RcsPropertyEntry[]} properties - Property entries attached to this node
 */

/**
 * Options for bulk property retrieval.
 * @typedef {Object} BulkPropertyOptions
 * @property {boolean} [ignoreHidden] - When true, omit hidden properties from results
 * @property {string[]} [propFilter] - When set, only include properties whose displayName is in this list
 */

/**
 * Create a single {@link RcsPropertyEntry} for the RCS property database.
 *
 * @param {string} name - UI label shown in the property panel
 * @param {string|number} value - Value shown in the property panel
 * @param {string} [category="Item"] - Grouping category in the property panel
 * @param {number} [type=20] - Autodesk property data type code
 * @param {string} [attributeName] - Internal attribute key; defaults to `name` when omitted
 * @returns {RcsPropertyEntry}
 */
export function prop(name, value, category = "Item", type = 20, attributeName = name) {
  return {
    displayName: name,
    displayValue: value,
    displayCategory: category,
    attributeName: attributeName,
    type,
    units: null,
    hidden: false,
    precision: 0
  };
}

/**
 * Trivial Property Database Loader for RCS Point Cloud files.
 *
 * @class
 */
export class RcsPropDbLoader {

  /**
   * @param {Model} model - The model instance
   * @param {EventDispatcher} eventTarget - Event target for dispatching events (usually the viewer API)
   */
  constructor(model, eventTarget) {
    /** @type {EventDispatcher} - Event target for dispatching property-DB events */
    this.eventTarget = eventTarget || new EventDispatcher();
    /** @type {Model} - The viewer model instance */
    this.model = model;
    /** @type {RcsPackage} - RCS package data backing the model */
    this.rcs = /** @type {RcsPackage} */model && model.getData();

    /** @type {ObjectTree|null} - Built instance tree, or null before load completes */
    this.instanceTree = null;
    /** @type {boolean} - True once the property database has been built (or intentionally skipped) */
    this.hasObjectProperties = false;
    /** @type {Error|{ propDbWasUnloaded: boolean }|null} - Load error, or null on success */
    this.propertyDbError = null;
    /** @type {number} - Load progress percentage (0–100) */
    this.loadProgress = 0;

    /** @type {number} - DbId of the instance-tree root used for traversal and search */
    this.rootDbId = 1;
    /** @type {number} - Total fragment count from the RCS package */
    this.numFragments = this.rcs.fragments?.length || 0;

    /** @type {Map<number, RcsPropertyData>} - Lazily populated cache of property data keyed by dbId */
    this.propertyDataCache = new Map();
  }

  /**
   * Cleanup resources
   */
  dtor() {
    // Fail any subsequent getObjectTree() calls on a stale loader reference.
    if (!this.propertyDbError && this.hasObjectProperties) {
      /** @type {{ propDbWasUnloaded: boolean }} */
      const unloadError = { propDbWasUnloaded: true };
      this.propertyDbError = unloadError;
    }
    this.propertyDataCache.clear();
    // Drop our reference to the tree (do NOT dispose it — the Model owns it via
    // model.getData().instanceTree and disposes it in Model.#dispose()).
    // This mirrors the behavior of PropDbLoader.
    this.instanceTree = null;
    // @ts-expect-error is null
    this.model = null;
    // @ts-expect-error is null
    this.rcs = null;
    // @ts-expect-error is null
    this.eventTarget = null;
  }

  /**
   * Get property data for a given dbId (creates on demand).
   * @param {number} dbId
   * @returns {RcsPropertyData|null}
   * @private
   */
  _getPropertyData(dbId) {
    if (this.propertyDataCache.has(dbId)) {
      return this.propertyDataCache.get(dbId) ?? null;
    }

    const propertyData = this._createPropertyData(dbId);

    if (propertyData) {
      this.propertyDataCache.set(dbId, propertyData);
    }
    return propertyData;
  }

  /**
   * @param {number} _dbId
   * @returns {RcsPropertyData|null}
   * @protected
   */
  _createPropertyData(_dbId) {
    throw new Error('RcsPropDbLoader._createPropertyData must be implemented by subclass');
  }

  /**
   * @param {InstanceTreeStorage} _storage
   * @returns {ObjectTree}
   * @protected
   */
  _buildInstanceTree(_storage) {
    throw new Error('RcsPropDbLoader._buildInstanceTree must be implemented by subclass');
  }

  /**
   * Load the property database.
   */
  load() {
    try {
      const storage = new InstanceTreeStorage();

      // Empty / placeholder models (see RcsPackage._initEmptyModel) have no
      // fragments and nothing to index — skip building an instance tree, but
      // still mark the load as complete (100%) and dispatch a terminal event.
      // Otherwise isLoadDone() stays false forever and waitForLoadDone() hangs.
      if (this.numFragments === 0) {
        this.onLoadProgress(100);
        this.eventTarget.dispatchEvent({
          type: et.OBJECT_TREE_UNAVAILABLE_EVENT,
          svf: this.rcs,
          model: this.model,
          target: this
        });
        return;
      }

      this.instanceTree = this._buildInstanceTree(storage);

      // Set fragment list for bounding box computation
      if (this.model) {
        this.instanceTree.setFragmentList(this.model.getFragmentList());
      }

      this.hasObjectProperties = true;

      if (this.rcs) {
        this.rcs.instanceTree = this.instanceTree;
        this.rcs.hasObjectProperties = true;
      }

      // Signal completion
      this.onLoadProgress(100);

      // Dispatch success event
      this.eventTarget.dispatchEvent({
        type: et.OBJECT_TREE_CREATED_EVENT,
        svf: this.rcs,
        model: this.model,
        target: this
      });

    } catch (error) {
      this.propertyDbError = error;
      this.onLoadProgress(100);

      this.eventTarget.dispatchEvent({
        type: et.OBJECT_TREE_UNAVAILABLE_EVENT,
        svf: this.rcs,
        model: this.model,
        target: this
      });

      console.error("Failed to create RCS property database:", error);
    }
  }

  /**
   * Check if the object tree is loaded
   * @returns {boolean}
   */
  isObjectTreeLoaded() {
    return !!this.instanceTree;
  }

  /**
   * Get the object tree
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  getObjectTree(onSuccess, onError) {
    if (this.instanceTree) {
      onSuccess(this.instanceTree);
    } else if (this.propertyDbError) {
      if (onError) {
        onError(this.propertyDbError);
      }
    } else if (!this.model) {
      if (onError) {
        onError({ propDbWasUnloaded: true });
      }
    } else if (onError) {
      // load() is synchronous — if we reach here, there is no object tree (e.g. empty model).
      onError({ code: 'NOT_AVAILABLE', msg: 'RCS property database has no object tree' });
    }
  }

  /**
   * Get properties for a database ID
   * @param {number} dbId - Database ID
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  getProperties(dbId, onSuccess, onError) {
    this.getProperties2(dbId, onSuccess, onError, {});
  }

  /**
   * Get properties for a database ID (v2)
   * @param {number} dbId - Database ID
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   * @param {Object} options - Options
   */
  getProperties2(dbId, onSuccess, onError, options) {
    const propertyData = this._getPropertyData(dbId);

    if (propertyData) {
      if (onSuccess) {
        onSuccess(propertyData);
      }
    } else {
      if (onError) {
        onError({ code: 'NOT_FOUND', msg: `No properties found for dbId ${dbId}` });
      }
    }
  }

  /**
   * Bulk property retrieval
   * @param {number[]} dbIds
   * @param {string[]} [propFilter]
   * @param {Function} [onSuccess]
   * @param {Function} [onError]
   * @param {boolean} [ignoreHidden]
   */
  getBulkProperties(dbIds, propFilter, onSuccess, onError, ignoreHidden) {
    this.getBulkProperties2(dbIds, { ignoreHidden, propFilter }, onSuccess, onError);
  }

  /**
   * Bulk property retrieval (v2)
   * @param {number[]} dbIds
   * @param {BulkPropertyOptions} options
   * @param {Function} [onSuccess]
   * @param {Function} [onError]
   */
  getBulkProperties2(dbIds, options = {}, onSuccess, onError) {
    const results = [];

    for (const dbId of dbIds) {
      const propertyData = this._getPropertyData(dbId);

      if (propertyData) {
        let props = propertyData.properties;

        // Apply property filter if provided
        if (options.propFilter && options.propFilter.length > 0) {
          const pf = options.propFilter;
          props = props.filter((/** @type {RcsPropertyEntry} */p) => pf.includes(p.displayName));
        }

        if (options.ignoreHidden) {
          props = props.filter((/** @type {RcsPropertyEntry} */p) => !p.hidden);
        }

        results.push({
          dbId: propertyData.dbId,
          name: propertyData.name,
          externalId: propertyData.externalId,
          properties: props
        });
      }
    }

    if (onSuccess) {
      onSuccess(results);
    }
  }

  /**
   * Get property set for database IDs
   * @param {number[]} dbIds - Array of database IDs
   * @param {Object} options - Options
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  getPropertySet(dbIds, options = {}, onSuccess, onError) {
    /** @type {Object<string, Array<{dbId: number, displayValue: string|number, displayName: string, displayCategory: string}>>} */
    const result = {};

    for (const dbId of dbIds) {
      const propertyData = this._getPropertyData(dbId);

      if (propertyData) {
        for (const prop of propertyData.properties) {
          const key = `${prop.displayCategory}/${prop.displayName}`;
          if (!result[key]) {
            result[key] = [];
          }
          result[key].push({
            dbId: propertyData.dbId,
            displayValue: prop.displayValue,
            displayName: prop.displayName,
            displayCategory: prop.displayCategory
          });
        }
      }
    }

    if (onSuccess) {
      onSuccess(result);
    }
  }

  /**
   * Search properties
   * @param {string} searchText - Text to search for
   * @param {string[]} attributeNames - Attribute names to search in
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   * @param {Object} options - Search options
   */
  searchProperties(searchText, attributeNames, onSuccess, onError, options) {
    const results = [];
    const searchLower = searchText.toLowerCase();

    // Get all dbIds from the instance tree
    const allDbIds = this.instanceTree ? this._getAllDbIds() : [this.rootDbId];

    for (const dbId of allDbIds) {
      const propertyData = this._getPropertyData(dbId);

      if (propertyData) {
        const nameMatches = propertyData.name.toLowerCase().includes(searchLower);
        const propMatches = propertyData.properties.some(
          (/** @type {RcsPropertyEntry} */p) => String(p.displayValue).toLowerCase().includes(searchLower)
        );

        if (nameMatches || propMatches) {
          results.push(dbId);
        }
      }
    }

    if (onSuccess) {
      onSuccess(results);
    }
  }

  /**
   * Get all dbIds from the instance tree
   * @returns {number[]} Array of all dbIds
   * @private
   */
  _getAllDbIds() {
    const dbIds = [this.rootDbId];

    // Enumerate all nodes in the tree
    if (this.instanceTree) {
      this.instanceTree.enumNodeChildren(this.rootDbId, (/** @type {number} */childDbId) => {
        dbIds.push(childDbId);
      }, true);
    }

    return dbIds;
  }

  /**
   * Find property by name
   * @param {string} propertyName - Property name to find
   * @returns {Promise<number[]>} Promise resolving to array of dbIds
   */
  findProperty(propertyName) {
    return new Promise((resolve) => {
      const matchingDbIds = [];
      const allDbIds = this.instanceTree ? this._getAllDbIds() : [this.rootDbId];

      for (const dbId of allDbIds) {
        const propertyData = this._getPropertyData(dbId);

        if (propertyData) {
          const hasProperty = propertyData.properties.some(
            (/** @type {RcsPropertyEntry} */p) => p.displayName === propertyName || p.attributeName === propertyName
          );

          if (hasProperty) {
            matchingDbIds.push(dbId);
          }
        }
      }

      resolve(matchingDbIds);
    });
  }

  /**
   * Get external ID mapping
   * @param {Function} onSuccess
   * @param {Function} [onError]
   * @param {Object<string, boolean>} [extIdFilter]
   */
  getExternalIdMapping(onSuccess, onError, extIdFilter) {
    /** @type {Object<string, number>} */
    const mapping = {};
    const allDbIds = this.instanceTree ? this._getAllDbIds() : [this.rootDbId];

    for (const dbId of allDbIds) {
      const propertyData = this._getPropertyData(dbId);

      if (propertyData) {
        if (!extIdFilter || extIdFilter[propertyData.externalId]) {
          mapping[propertyData.externalId] = dbId;
        }
      }
    }

    if (onSuccess) {
      onSuccess(mapping);
    }

    return Promise.resolve(mapping);
  }

  /**
   * Get layer to node ID mapping (not applicable for point clouds)
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  getLayerToNodeIdMapping(onSuccess, onError) {
    if (onSuccess) {
      onSuccess({});
    }
  }

  /**
   * Find layers (not applicable for point clouds)
   * @returns {Promise<Object>}
   */
  findLayers() {
    return Promise.resolve({});
  }

  /**
   * User-provided function hook (intentional no-op for RCS; see implementation comment).
   * @param {Function|string} _code
   * @param {*} _userData
   * @returns {Promise<undefined>}
   */
  executeUserFunction(_code, _userData) {
    // RCS currently exposes too few properties for ad-hoc user code to be useful, and
    // executing string/function user input (previously via eval) is a security risk.
    return Promise.resolve(undefined);
  }

  /**
   * Get attribute definitions
   * @param {boolean} force
   * @returns {Promise<Object[]>}
   */
  async getAttributeDefinitions(force) {
    return [
    {
      name: "Name",
      category: "__name__",
      dataType: 20,
      dataTypeContext: undefined,
      description: undefined,
      flags: 0,
      precision: 0,
      propertyHash: "name_hash"
    },
    {
      name: "Type",
      category: "Item",
      dataType: 20,
      dataTypeContext: undefined,
      description: undefined,
      flags: 0,
      precision: 0,
      propertyHash: "type_hash"
    }];

  }

  /**
   * Load external IDs (no-op for RCS as IDs are already available)
   * @returns {Promise<void>}
   */
  loadExternalIds() {
    return Promise.resolve();
  }

  /**
   * Handle load progress
   * @param {number} percent - Progress percentage
   * @private
   */
  onLoadProgress(percent) {
    this.eventTarget.dispatchEvent({
      type: et.OBJECT_TREE_LOAD_PROGRESS_EVENT,
      percent,
      svf: this.rcs,
      model: this.model,
      target: this
    });
    this.loadProgress = percent;
  }

  /**
   * Get load progress
   * @returns {number} Progress in percent (0-100)
   */
  getLoadProgress() {
    return this.loadProgress;
  }

  /**
   * Check if loading is done
   * @returns {boolean}
   */
  isLoadDone() {
    return this.loadProgress === 100;
  }

  /**
   * Diff properties (not supported for RCS)
   * @param {Object} dbToDiff - Database to diff with
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  diffProperties(dbToDiff, onSuccess, onError) {
    if (onSuccess) {
      onSuccess({ changedIds: [] });
    }
  }

  /**
   * Async property operation (simplified for RCS)
   * @param {Object} opArgs - Operation arguments
   * @param {Function} success - Success callback
   * @param {Function} fail - Failure callback
   */
  asyncPropertyOperation(opArgs, success, fail) {
    // For RCS, most operations can be handled synchronously
    if (this.propertyDbError) {
      if (fail) {
        fail(this.propertyDbError);
      }
    } else if (this.instanceTree || this.hasObjectProperties) {
      if (success) {
        success({});
      }
    } else if (fail) {
      // Tree is not available yet and no error has been recorded — fail
      // rather than leaving the caller hanging with no callback.
      fail({ code: 'NOT_LOADED', msg: 'Property database is not available yet' });
    }
  }
}